/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useEffect, useRef, useState} from 'react'
import db, {storage} from '../../../../services/firebase'
import {ref, uploadBytesResumable, getDownloadURL} from 'firebase/storage'
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  runTransaction,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore'
import {deleteObject, ref as storageRef} from 'firebase/storage'
import {useAuth} from '../../../modules/auth'
import Drawer from 'react-modern-drawer'
import Dropzone from 'react-dropzone'
import {ToastContainer} from 'react-toastify'
import {runControlTests} from 'app/pages/controls/utils/ControlLogics'
import {showErrorToast, showSuccessToast} from 'app/components/ShowToast'
import ConfirmationModal from 'app/components/ConfirmationModal'
import {FileItemType} from '../Utils/PolicyUtils'
import FormatTimestamp from 'app/components/FormatTimestamp'
import UpgradePlan from 'app/components/UpgradePlan'
import Button from 'app/components/Button'
import DocumentViewerModal from 'app/components/DocumentViewerModal'

export interface policy {
  msg: string
  message: string
  fileUrl?: any
}

type FileDataType = {
  name: string
  url: string
  date: string
  lastModifiedDate?: Date
  size: number
}

interface ViewProps {
  isOpen: boolean
  onClose: () => void
  id: string
}

const PolicyDrawer: React.FC<ViewProps> = ({isOpen, onClose, id}) => {
  const {currentUser} = useAuth()
  const [owner, setOwner] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const drawerRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [owners, setOwners] = useState<any[]>([])
  const [policyTemplate, setPolicyTemplate] = useState<any[]>([])
  const [reviewDate, setReviewDate] = useState('')
  const [customfileUrl, setCustomfileUrl] = useState<FileDataType[]>([])
  const [isDropdownVisible, setDropdownVisible] = useState(false)
  const [status, setStatus] = useState('')
  const statusRef = useRef(status)
  const [errorMessages, setErrorMessages] = useState<{[key: string]: string}>({})
  const [localFileInfo, setLocalFileInfo] = useState<FileDataType[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [policyName, setPolicyName] = useState('')
  const [date, setDate] = useState('')
  const [isFileUploading, setIsFileUploading] = useState(false)
  const [testId, setTestId] = useState('')
  const [timeframe, setTimeframe] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [currentOwner, setCurrentOwner] = useState('')
  const [isUpdatingOwner, setIsUpdatingOwner] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [selectedPolicyToDelete, setSelectedPolicyToDelete] = useState<FileItemType | null>(null)
  const [isEvidenceDeleteOpen, setIsEvidenceDeleteOpen] = useState(false)
  const [isPolicyDeleteOpen, setIsPolicyDeleteOpen] = useState(false)
  const [fileToDelete, setFileToDelete] = useState<{index: number; file: any; id: string} | null>(
    null
  )
  const [showDocumentViewer, setShowDocumentViewer] = useState(false)
  const [selectedFile, setSelectedFile] = useState<{
    url: string
    name: string
  } | null>(null)

  const findOwnerUid = (ownerName: string) => {
    const selectedOwner = owners.find(owner => owner.full_name === ownerName)
    return selectedOwner?.uid || selectedOwner?.id || ""
  }

  useEffect(() => {
    statusRef.current = status
  }, [status])

  useEffect(() => {
    const checkReviewDate = () => {
      const today = new Date()
      const review = new Date(reviewDate)

      if (review <= today) {
        setErrorMessages((prev) => ({...prev, [id]: 'Please select a new review date.'}))
      } else {
        setErrorMessages((prev) => {
          const newErrors = {...prev}
          delete newErrors[id]
          return newErrors
        })
      }
    }

    if (reviewDate) {
      checkReviewDate()
    } else {
      setErrorMessages((prev) => {
        const newErrors = {...prev}
        delete newErrors[id]
        return newErrors
      })
    }
  }, [reviewDate, id])

  useEffect(() => {
    const unsubscribe = getPolicy()
    return () => {
      if (unsubscribe) unsubscribe()
    }
  }, [id])

  useEffect(() => {
    if (owner && currentUser?.userName) {
      setCurrentOwner(owner)
    }
  }, [owner, currentUser?.userName])

  useEffect(() => {
    if (isOpen && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0
    }
  }, [isOpen, id])

  const getPolicy = () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      if (!tenantId || !id) {
        return
      }
      const tenantRef = doc(db, 'tenants', tenantId)
      const PolicyRef = collection(tenantRef, 'policies')

      const unsubscribe = onSnapshot(doc(PolicyRef, id), (snapshot) => {
        const policyData = snapshot.data()
        if (policyData) {
          setPolicyTemplate([{...policyData, id: snapshot.id}])
          setOwner(policyData.owner || '')
          setReviewDate(policyData.review_date || '')
          setCustomfileUrl(policyData.customfile || '')
          setLocalFileInfo(policyData.customfile || '')
          determineAndUpdateStatus(policyData)
          setPolicyName(policyData.policy || '')
          setTestId(policyData.testId || '')
          setTimeframe(policyData.timeframe || 'Annually')
        }
      })

      return unsubscribe
    } catch (error) {
      console.error('Error getting policy:', error)
    }
  }

  const checkControl = async () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const result = await runControlTests(tenantId)
    } catch (error) {
      console.error('Error checking control:', error)
    }
  }

  const determineAndUpdateStatus = (policyData) => {
    const {owner, review_date, customfile, status} = policyData
    const today = new Date()
    const review = new Date(review_date)

    let newStatus = status

    if (review <= today) {
      newStatus = 'Needs review'
    } else if (owner && review_date && customfile && customfile.length > 0) {
      if (status !== 'Approved') {
        newStatus = 'Needs approval'
      }
    } else {
      newStatus = 'Incomplete'
    }

    if (newStatus !== status) {
      setStatus(newStatus)
      updatePolicy({status: newStatus})
      checkControl()
    }
  }

  useEffect(() => {
    const updateTestStatus = async () => {
      if (testId) {
        if (!currentUser?.tenantId || !id) return
        if ((status === 'Incomplete' || status === 'Needs review') && testId) {
          const testDocRef = doc(db, `tenants/${currentUser.tenantId}/tests/${testId}`)
          const testDocSnapshot = await getDoc(testDocRef)

          if (testDocSnapshot.exists()) {
            await updateDoc(testDocRef, {
              status: 'Fail',
            })
          }
        }
      }
    }

    updateTestStatus()
  }, [status])

  useEffect(() => {
    const oneYearFromNow = new Date()
    oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1)
    setDate(oneYearFromNow.toISOString().slice(0, 10))
  }, [])

  const updatePolicy = async (updateData) => {
    if (!currentUser?.tenantId || !id) return

    const policyRef = doc(db, 'tenants', currentUser.tenantId, 'policies', id)

    try {
      await runTransaction(db, async (transaction) => {
        const policyDoc = await transaction.get(policyRef)
        if (!policyDoc.exists()) {
          throw new Error('Document does not exist!')
        }
        transaction.update(policyRef, {
          ...updateData,
          updated_at: serverTimestamp(),
        })
      })
    } catch (error) {
      console.error('Transaction error:', error)
      await updateDoc(policyRef, {
        ...updateData,
        updated_at: serverTimestamp(),
      })
    } finally {
      setIsUploading(false)
    }
  }

  useEffect(() => {
    const tenantId = currentUser?.tenantId || ''
    const tenantRef = doc(db, 'tenants', tenantId)
    const PolicyRef = collection(tenantRef, 'employees')

    const unsubscribe = onSnapshot(PolicyRef, (snapshot) => {
      const risksData = snapshot.docs
        .map((doc) => ({
          ...doc.data(),
          id: doc.id,
          end_date: doc.data().end_date,
        }))
        .filter((data) => !data.end_date)
      setOwners(risksData)
    })

    return unsubscribe
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (drawerRef.current && !drawerRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const currentDate = new Date()
  const year = currentDate.getFullYear()
  const month = currentDate.getMonth() + 1
  const day = currentDate.getDate()

  const todaysDate = month + '/' + day + '/' + year

  const handleUpload = async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return
    const localFile = acceptedFiles[0]
    if (localFile.size > 10 * 1024 * 1024) {
      showErrorToast({message: 'File size exceeds 10MB limit. Please upload a smaller file.'})
      return
    }
    setIsFileUploading(true)
    setUploadProgress(1)
    setIsUploading(true)

    const newFileInfo: FileDataType = {
      name: localFile.name,
      size: localFile.size,
      date: new Date().toISOString(),
      url: URL.createObjectURL(localFile),
    }
    setLocalFileInfo((prevFiles) => [...(Array.isArray(prevFiles) ? prevFiles : []), newFileInfo])

    const fileName = localFile.name
    const fileRef = ref(
      storage,
      `tenants/${currentUser?.tenantId}/policy/policies_documents/${fileName}`
    )
    const uploadTask = uploadBytesResumable(fileRef, localFile)

    const progressInterval = setInterval(() => {
      setUploadProgress((prevProgress) => {
        if (prevProgress >= 90) {
          clearInterval(progressInterval)
          return prevProgress
        }
        return prevProgress + 10
      })
    }, 200)

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
        setUploadProgress(progress)
      },
      (error) => {
        setUploadProgress(0)
        setIsFileUploading(false)
        setIsUploading(false)
        showErrorToast({message: 'Error uploading file. Please try again.'})
      },
      async () => {
        try {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref)

          const updatedFileInfo = {
            ...newFileInfo,
            url: downloadURL,
          }

          setLocalFileInfo((prevFiles) =>
            Array.isArray(prevFiles)
              ? prevFiles.map((file) => (file.name === fileName ? updatedFileInfo : file))
              : [updatedFileInfo]
          )

          setCustomfileUrl((prevFiles) =>
            Array.isArray(prevFiles)
              ? prevFiles.map((file) => (file.name === fileName ? updatedFileInfo : file))
              : [updatedFileInfo]
          )

          updatePolicy({
            customfile: Array.isArray(customfileUrl)
              ? [
                  ...customfileUrl,
                  {
                    name: fileName,
                    url: downloadURL,
                    date: new Date().toISOString(),
                    size: localFile.size,
                  },
                ]
              : [
                  {
                    name: fileName,
                    url: downloadURL,
                    date: new Date().toISOString(),
                    size: localFile.size,
                  },
                ],
            review_date: date,
            timeframe: 'Annually',
          })
          setTimeout(() => {
            setUploadProgress(0)
            setIsFileUploading(false)
            setIsUploading(false)
          }, 500)
        } catch (error) {
          setIsFileUploading(false)
          setIsUploading(false)
        } finally {
          setIsLoading(false)
          setUploadProgress(0)
          setIsFileUploading(false)
          setIsUploading(false)
        }
      }
    )
  }

  const handleApprove = async () => {
    try {
      setIsLoading(true)
      await updatePolicy({status: 'Approved', last_approved: new Date().toISOString()})
      if (testId) {
        if (!currentUser || !id || updatePolicy.length === 0) return

        const tenantId = currentUser?.tenantId || ''
        try {
          const testDocRef = doc(db, `tenants/${tenantId}/tests/${testId}`)
          const testDocSnapshot = await getDoc(testDocRef)

          if (testDocSnapshot.exists()) {
            await updateDoc(testDocRef, {
              status: 'Pass',
              owner: owner,
              timeframe: timeframe,
              next_review_date: reviewDate ? reviewDate : date,
            })
          }
          showSuccessToast({message: 'Policy approved successfully!'})
        } catch (error) {
          console.error('Error updating document status: ', error)
        }
      }
      setStatus('Approved')
      const tenantRef = doc(db, 'tenants', currentUser?.tenantId!)
      const activitiesRef = collection(tenantRef, 'activities')
      await addDoc(activitiesRef, {
        message: `${policyName} has been approved by ${currentUser?.userName}`,
        timestamp: serverTimestamp(),
        tenantId: currentUser?.tenantId,
        // email: email,
        uid: currentUser?.uid,
        username: currentUser?.userName,
        action: 'Policy updated',
        target: 'Policies',
        email: currentUser?.email,
        date: todaysDate,
      })
    } catch (error) {
      showErrorToast({message: 'Error approving policy!'})
    }
    setIsLoading(false)
  }

  const handleRemoveFileClick = (index: number, file: any, id: string) => {
    setFileToDelete({index, file, id})
    setIsEvidenceDeleteOpen(true)
  }

  const handleConfirmRemoveFile = async () => {
    if (!fileToDelete) return

    const {index, file, id} = fileToDelete
    const fileToRemove = localFileInfo[index]
    const updatedFiles = localFileInfo.filter((_, i) => i !== index)
    setLocalFileInfo(updatedFiles)
    setIsDeleting(true)

    try {
      const tenantId = currentUser?.tenantId
      if (!tenantId || !id) {
        return
      }

      const policyRef = doc(db, 'tenants', tenantId, 'policies', id)
      await updateDoc(policyRef, {
        customfile: updatedFiles,
        review_date: '',
      })

      const fileRef = storageRef(
        storage,
        `tenants/${currentUser?.tenantId}/policy/policies_documents/${fileToRemove.name}`
      )
      await deleteObject(fileRef)
      showSuccessToast({message: 'Evidence removed successfully!'})
    } catch (error) {
      setLocalFileInfo((prevFiles) => [...prevFiles, fileToRemove])
    } finally {
      setIsDeleting(false)
      setIsEvidenceDeleteOpen(false)
      setFileToDelete(null)
    }
  }

  const toggleDropdown = () => {
    setDropdownVisible(!isDropdownVisible)
  }

  const handleTimeframe = async (event) => {
    const selectedTimeframe = event.target.value
    setTimeframe(selectedTimeframe)
    updatePolicy({timeframe: selectedTimeframe})

    // Calculate the next review date
    const today = new Date()
    let nextReviewDate

    switch (selectedTimeframe) {
      case 'Annually':
        nextReviewDate = new Date(today.setFullYear(today.getFullYear() + 1))
        break
      case 'Bi-annually':
        nextReviewDate = new Date(today.setMonth(today.getMonth() + 6))
        break
      case 'Quarterly':
        nextReviewDate = new Date(today.setMonth(today.getMonth() + 3))
        break
      case 'Monthly':
        nextReviewDate = new Date(today.setMonth(today.getMonth() + 1))
        break
      case 'Weekly':
        nextReviewDate = new Date(today.setDate(today.getDate() + 7))
        break
      case 'Never':
        nextReviewDate = null
        break
      default:
        nextReviewDate = null
    }

    if (nextReviewDate) {
      const formattedDate = nextReviewDate.toISOString().split('T')[0]
      setReviewDate(formattedDate)
      updatePolicy({review_date: formattedDate})
    } else {
      setReviewDate('')
    }
  }

  const handleDownloadClick2 = async (customfileUrl) => {
    try {
      const fileUrl = customfileUrl.url

      const response = await fetch(fileUrl)
      const fileContent = await response.blob()

      const blob = new Blob([fileContent], {type: 'application/octet-stream'})

      const blobUrl = URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = blobUrl
      link.download = `${customfileUrl.name}`

      document.body.appendChild(link)

      link.click()

      document.body.removeChild(link)
      URL.revokeObjectURL(blobUrl)

      setDropdownVisible(false)
    } catch (error) {
      console.error('Error downloading policy file:', error)
    }
  }

  const handleViewClick = (customfileUrl) => {
    const fileUrl = customfileUrl.url
    const fileName = customfileUrl.name
    
    const supportedTypes = ['pdf', 'docx', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'svg']
    const fileExtension = fileName.split('.').pop()?.toLowerCase()
    
    if (fileExtension && supportedTypes.includes(fileExtension)) {
      setSelectedFile({
        url: fileUrl,
        name: fileName
      })
      setShowDocumentViewer(true)
    } else {
      const link = document.createElement('a')
      link.href = fileUrl
      link.download = fileName
      link.click()
    }

    setDropdownVisible(false)
  }

  const handleOwnerChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newOwner = e.target.value
    setOwner(newOwner)
    if (!newOwner) return
    try {
      setIsUpdatingOwner(true)
      const policyRef = doc(db, 'tenants', currentUser?.tenantId!, 'policies', id)
      await runTransaction(db, async (transaction) => {
        const policyDoc = await transaction.get(policyRef)
        if (!policyDoc.exists()) {
          throw new Error('Document does not exist!')
        }

        transaction.update(policyRef, {
          owner: newOwner,
          owner_uid: findOwnerUid(newOwner),
          updated_at: serverTimestamp(),
        })
      })

      setCurrentOwner(newOwner.toLowerCase())
      const tenantRef = doc(db, 'tenants', currentUser?.tenantId!)
      const activitiesRef = collection(tenantRef, 'activities')
      await addDoc(activitiesRef, {
        message: `Assigned the ${policyTemplate[0].policy} policy owner to ${newOwner} by ${currentUser?.userName}`,
        timestamp: serverTimestamp(),
        action: 'Policy updated',
        target: 'Policies',
        tenantId: currentUser?.tenantId,
        uid: currentUser?.uid,
        username: currentUser?.userName,
        email: currentUser?.email,
        date: todaysDate,
      })
    } catch (error) {
      console.error('Error updating owner:', error)
    } finally {
      setIsUpdatingOwner(false)
    }
  }

  function bytesToKB(bytes) {
    return (bytes / 1024).toFixed(2)
  }

  const handleDownloadClick = async (policyData) => {
    try {
      const fileUrl = policyData.fileUrl
      const response = await fetch(fileUrl)
      const fileContent = await response.blob()
      let fileExtension = 'docx'
      if (policyData.fileName) {
        fileExtension = policyData.fileName.split('.').pop()
      } else if (fileUrl.includes('.xlsx')) {
        fileExtension = 'xlsx'
      }
      const blob = new Blob([fileContent], {type: 'application/octet-stream'})
      const blobUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = `${
        policyData.fileName ? policyData.fileName : policyData.policy
      }.${fileExtension}`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(blobUrl)
    } catch (error) {
      console.error('Error downloading policy file:', error)
    }
  }

  const handleDeleteClick = (policyData: FileItemType) => {
    setSelectedPolicyToDelete(policyData)
    setIsPolicyDeleteOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!selectedPolicyToDelete) return
    setIsDeleting(true)
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const policyRef = collection(tenantRef, 'policies')
      const docRef = doc(policyRef, selectedPolicyToDelete.id)
      await deleteDoc(docRef)
      const testsCollectionRef = collection(tenantRef, 'tests')
      const snapshot = await getDocs(testsCollectionRef)
      const testsData = snapshot.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
        policies: doc.data().policies,
      }))
      const filteredTests = testsData.filter(
        (test) => test?.policies === selectedPolicyToDelete.policy
      )
      if (filteredTests.length > 0) {
        for (const test of filteredTests) {
          const testDocRef = doc(tenantRef, 'tests', test.id)
          try {
            await updateDoc(testDocRef, {status: 'Fail'})
          } catch (error) {
            console.error(`Error updating test ${test.id}:`, error)
          }
        }
      }

      getPolicy()
      setIsPolicyDeleteOpen(false)
      setSelectedPolicyToDelete(null)
      showSuccessToast({message: `Policy "${selectedPolicyToDelete.policy}" has been deleted.`})
      onClose()
    } catch (error) {
      showErrorToast({message: 'An error occurred while deleting the policy.'})
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <>
      <Drawer open={isOpen} onClose={onClose} direction='right' className='p-0' size='45%'>
        {policyTemplate.map((temp, index) => (
          <>
            <div
              className='border-bottom border-gray-300'
              style={{
                position: 'sticky',
                top: 0,
                backgroundColor: 'white',
                padding: '14px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                paddingLeft: '15px',
                paddingRight: '15px',
              }}
            >
              <div className='fs-4 fw-bold' style={{margin: 0}}>
                {temp.policy}
                {(isUploading || isUpdatingOwner) && (
                  <span
                    className='badge px-3 rounded-pill ms-4'
                    style={{border: '2px solid #E1E1E1'}}
                  >
                    <span className='fs-7'>Updating...</span>
                  </span>
                )}
              </div>

              <div style={{display: 'flex', gap: '8px'}}>
                {temp.author === 'Custom policy' && (
                  <button
                    className='btn btn-icon btn-active-light-danger  btn-active-color-danger btn-sm '
                    onClick={(e) => handleDeleteClick(temp)}
                    disabled={currentUser?.role === 'Auditor' || temp.author !== 'Custom policy'}
                    style={{
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      if (e.currentTarget) {
                        e.currentTarget.style.backgroundColor = '#fff'
                        const svgElement = e.currentTarget.querySelector('svg')
                        if (svgElement) {
                          svgElement.style.stroke = 'oklch(57.7% 0.245 27.325)'
                        }
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (e.currentTarget) {
                        e.currentTarget.style.backgroundColor = '#fff'
                        const svgElement = e.currentTarget.querySelector('svg')
                        if (svgElement) {
                          svgElement.style.stroke = '#5A5A5A'
                        }
                      }
                    }}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='20'
                      height='20'
                      viewBox='0 0 24 24'
                      fill='none'
                      stroke='currentColor'
                      stroke-width='2'
                      stroke-linecap='round'
                      stroke-linejoin='round'
                      className='icon icon-tabler icons-tabler-outline icon-tabler-trash'
                    >
                      <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                      <path d='M4 7l16 0' />
                      <path d='M10 11l0 6' />
                      <path d='M14 11l0 6' />
                      <path d='M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12' />
                      <path d='M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3' />
                    </svg>
                  </button>
                )}
                <div
                  className='btn btn-icon btn-sm btn-active-light-primary'
                  aria-label='Close'
                  onClick={onClose}
                  style={{
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    if (e.currentTarget) {
                      e.currentTarget.style.backgroundColor = '#fff'
                      const svgElement = e.currentTarget.querySelector('svg')
                      if (svgElement) {
                        svgElement.style.stroke = '#0E9F6E'
                      }
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (e.currentTarget) {
                      e.currentTarget.style.backgroundColor = '#fff'
                      const svgElement = e.currentTarget.querySelector('svg')
                      if (svgElement) {
                        svgElement.style.stroke = '#5A5A5A'
                      }
                    }
                  }}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='20'
                    height='20'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='#5A5A5A'
                    stroke-width='2'
                    stroke-linecap='round'
                    stroke-linejoin='round'
                    className='icon icon-tabler icons-tabler-outline icon-tabler-x'
                  >
                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                    <path d='M18 6l-12 12' />
                    <path d='M6 6l12 12' />
                  </svg>
                </div>
              </div>
            </div>

            <div
              ref={scrollContainerRef}
              className='scroll-y overflow-auto px-5 py-5'
              tabIndex={-1}
              style={{height: 'calc(100vh - 40px)'}}
            >
              <div className='card mb-8 p-4' style={{background: '#F6F6F6'}}>
                {temp.status === 'Incomplete' ? (
                  <>
                    <span
                      className='badge rounded-pill p-1 mt-2'
                      style={{border: '1px solid #C5C5C5', width: '110px'}}
                    >
                      <span className='mx-2'>
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          width='18'
                          height='18'
                          viewBox='0 0 24 24'
                          fill='none'
                          stroke='#EE3043'
                          strokeWidth='2'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          className='icon icon-tabler icons-tabler-outline icon-tabler-xbox-x mb-1'
                        >
                          <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                          <path d='M12 21a9 9 0 0 0 9 -9a9 9 0 0 0 -9 -9a9 9 0 0 0 -9 9a9 9 0 0 0 9 9z' />
                          <path d='M9 8l6 8' />
                          <path d='M15 8l-6 8' />
                        </svg>
                      </span>
                      <span className='fs-7'>Incomplete</span>
                    </span>
                    <div className='fs-6 fw-semibold my-4'>
                      Please upload your organization's {temp.policy} or you can also use the
                      template below.
                    </div>
                  </>
                ) : temp.status === 'Needs approval' ? (
                  <>
                    <span
                      className='badge rounded-pill p-1 mt-2'
                      style={{border: '1px solid #C5C5C5', width: '140px'}}
                    >
                      <span className='mx-2'>
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          width='18'
                          height='18'
                          viewBox='0 0 24 24'
                          fill='none'
                          stroke='#FFA800'
                          strokeWidth='2'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          className='icon icon-tabler icons-tabler-outline icon-tabler-file-check mb-1'
                        >
                          <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                          <path d='M14 3v4a1 1 0 0 0 1 1h4' />
                          <path d='M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z' />
                          <path d='M9 15l2 2l4 -4' />
                        </svg>
                      </span>
                      <span className='fs-7'>Needs Approval</span>
                    </span>
                    <div className='fs-6 fw-semibold my-4'>
                      Owner approval is pending for this policy. <strong>Note:</strong> Please
                      assign owner if not assigned yet.
                    </div>
                    {currentUser?.userName?.toLowerCase().trim() ===
                      currentOwner?.toLowerCase().trim() &&
                      (isLoading ? (
                        <button
                          type='button'
                          // onClick={handleApprove}
                          className='btn btn-sm fs-6'
                          style={{
                            height: '40px',
                            width: '150px',
                            background: '#0E9F6E',
                            color: '#fff',
                          }}
                          disabled
                        >
                          Approving...
                          <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                        </button>
                      ) : (
                        <button
                          type='button'
                          onClick={handleApprove}
                          className='btn btn-sm fs-6'
                          style={{
                            height: '40px',
                            width: '100px',
                            background: '#0E9F6E',
                            color: '#fff',
                          }}
                          onMouseEnter={(e: React.MouseEvent<HTMLElement>) =>
                            (e.currentTarget.style.backgroundColor = '#148363')
                          }
                          onMouseLeave={(e: React.MouseEvent<HTMLElement>) =>
                            (e.currentTarget.style.backgroundColor = '#0E9F6E')
                          }
                        >
                          Approve
                        </button>
                      ))}
                  </>
                ) : temp.status === 'Needs review' ? (
                  <>
                    <span
                      className='badge rounded-pill p-1 mt-2'
                      style={{border: '1px solid #C5C5C5', width: '130px'}}
                    >
                      <span className='mx-2'>
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          width='20.'
                          height='20'
                          viewBox='0 0 24 24'
                          fill='none'
                          stroke='#0075FF'
                          stroke-width='2'
                          stroke-linecap='round'
                          stroke-linejoin='round'
                          className='icon icon-tabler icons-tabler-outline icon-tabler-calendar mb-1'
                        >
                          <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                          <path d='M4 7a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2v-12z' />
                          <path d='M16 3v4' />
                          <path d='M8 3v4' />
                          <path d='M4 11h16' />
                          <path d='M11 15h1' />
                          <path d='M12 15v3' />
                        </svg>
                      </span>
                      <span className='fs-7'>Needs review</span>
                    </span>
                    <div className='fs-6 fw-semibold my-4'>
                      Policy review overdue. Please upload updated policy, set next review date, and
                      re-approve to maintain compliance.
                    </div>
                  </>
                ) : (
                  temp.status === 'Approved' && (
                    <>
                      <span
                        className='badge rounded-pill p-1 mt-2'
                        style={{border: '1px solid #C5C5C5', width: '110px'}}
                      >
                        <span className='mx-2'>
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            width='20'
                            height='20'
                            viewBox='0 0 24 24'
                            fill='none'
                            stroke='#0E9F6E'
                            stroke-width='2'
                            stroke-linecap='round'
                            stroke-linejoin='round'
                            className='icon icon-tabler icons-tabler-outline icon-tabler-circle-check mb-1'
                          >
                            <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                            <path d='M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0' />
                            <path d='M9 12l2 2l4 -4' />
                          </svg>
                        </span>
                        <span className='fs-7'>Approved</span>
                      </span>
                      <div className='fs-6 fw-semibold my-4'>
                        This policy has been approved by the owner. Employees can now read and
                        accept this policy.
                      </div>
                    </>
                  )
                )}
              </div>

              <div className='mt-4'>
                <div className='fw-bold fs-6 '>Template</div>

                {temp.fileUrl.length === 0 && temp.fileSize === '' ? (
                  <div className='card mb-4 mb-xl-8 mt-2 p-4' style={{background: '#F6F6F6'}}>
                    <div className='mt-1'>
                      <span className='fw-bold fs-6'>Template unavailabe</span>
                    </div>

                    <div className='fs-6 mt-2 mb-2' style={{color: '#5A5A5A'}}>
                      Template is not provided for this policy <strong>[ {temp.policy} ] </strong>.
                    </div>
                  </div>
                ) : (
                  <div className={`card mb-4 mb-xl-8 mt-2`}>
                    <div
                      className='card-header border-0 pt-6 mb-5'
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <div style={{display: 'flex', alignItems: 'center', flex: 1, minWidth: 0}}>
                        <img
                          src='/media/icon/file2.svg'
                          alt=''
                          className='img-fluid'
                          style={{width: '50px', height: '50px', marginRight: '10px'}}
                        />
                        <div
                          className='d-flex justify-content-start flex-column fw-bold fs-6'
                          style={{flexGrow: 1, minWidth: 0}}
                        >
                          <span style={{wordBreak: 'break-word', whiteSpace: 'normal'}}>
                            {temp.fileName ? temp.fileName : temp.policy}
                          </span>
                          <span className='fw-semibold d-block fs-7' style={{color: '#5A5A5A'}}>
                            Size: {temp.fileSize} KB
                          </span>
                        </div>
                      </div>
                      <div
                        style={{
                          marginLeft: '20px',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <Button
                          btnType='transparent'
                          className='text-dark fw-semibold text-hover-primary btn-active-light-primary btn btn-sm'
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()

                            if (currentUser?.account_type === 'Trial' || currentUser?.account_type === 'Trial-plus') {
                              setShowModal(true)
                            } else {
                              handleDownloadClick(temp)
                            }
                          }}
                          disabled={currentUser?.role === 'Auditor'}
                        >
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            width='20'
                            height='20'
                            viewBox='0 0 24 24'
                            fill='none'
                            stroke='currentColor'
                            strokeWidth='2'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            className='icon icon-tabler icons-tabler-outline icon-tabler-cloud-download mx-2 mb-1'
                          >
                            <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                            <path d='M19 18a3.5 3.5 0 0 0 0 -7h-1a5 4.5 0 0 0 -11 -2a4.6 4.4 0 0 0 -2.1 8.4' />
                            <path d='M12 13l0 9' />
                            <path d='M9 19l3 3l3 -3' />
                          </svg>
                          Download
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className='separator my-8'></div>

              <div className='form'>
                <div className='form-group mb-4'>
                  <div className='main-container'>
                    <div className='fw-bold fs-6 my-2'>Uploads</div>
                    {localFileInfo && localFileInfo.length > 0 && (
                      <>
                        {localFileInfo.map((file, index) => (
                          <React.Fragment key={index}>
                            <div className={`card mb-4 mb-xl-8 mt-4`}>
                              <div
                                className='card-header border-0 pt-6 mb-5'
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                }}
                              >
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    flex: 1,
                                    minWidth: 0,
                                  }}
                                >
                                  <img
                                    src='/media/icon/file2.svg'
                                    alt=''
                                    className='img-fluid'
                                    style={{width: '50px', height: '50px', marginRight: '10px'}}
                                  />
                                  <div
                                    className='d-flex justify-content-start flex-column fw-bold fs-6'
                                    style={{flexGrow: 1, minWidth: 0}}
                                  >
                                    <span
                                      style={{
                                        wordBreak: 'break-word',
                                        whiteSpace: 'normal',
                                        cursor: 'pointer',
                                      }}
                                      className='text-hover-primary'
                                      onClick={() => handleViewClick(file)}
                                    >
                                      {file.name}
                                    </span>
                                    <span
                                      className='fw-semibold d-block fs-7'
                                      style={{color: '#5A5A5A'}}
                                    >
                                      Size: {bytesToKB(file.size)} KB | Uploaded: {FormatTimestamp({timestamp: file.date})}
                                    </span>
                                  </div>
                                </div>

                                <div className='dropdown '>
                                  {uploadProgress > 0 && index === localFileInfo.length - 1 ? (
                                    <div>
                                      <span>{uploadProgress}%</span>
                                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                                    </div>
                                  ) : (
                                    <>
                                      <button
                                        className='btn  border-0 p-0'
                                        type='button'
                                        id={`dropdownMenu${index}`}
                                        data-bs-toggle='dropdown'
                                        aria-haspopup='true'
                                        aria-expanded='false'
                                      >
                                        <img
                                          src='/media/buttons/Options.svg'
                                          alt='Options'
                                          onClick={toggleDropdown}
                                        />
                                      </button>
                                      <ul
                                        className='dropdown-menu'
                                        aria-labelledby={`dropdownMenu${index}`}
                                      >
                                        <li>
                                          <button
                                            className='dropdown-item text-hover-primary'
                                            type='button'
                                            style={{color: '#262626'}}
                                            onClick={() => handleViewClick(file)}
                                          >
                                            <svg
                                              xmlns='http://www.w3.org/2000/svg'
                                              width='18'
                                              height='18'
                                              viewBox='0 0 24 24'
                                              fill='none'
                                              stroke='currentColor'
                                              stroke-width='2'
                                              stroke-linecap='round'
                                              stroke-linejoin='round'
                                              className='icon icon-tabler icons-tabler-outline icon-tabler-eye mx-1'
                                            >
                                              <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                              <path d='M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0' />
                                              <path d='M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6' />
                                            </svg>{' '}
                                            View
                                          </button>
                                        </li>
                                        <li>
                                          <button
                                            className='dropdown-item text-hover-primary'
                                            type='button'
                                            style={{color: '#262626'}}
                                            onClick={(e) => handleDownloadClick2(file)}
                                          >
                                            <svg
                                              xmlns='http://www.w3.org/2000/svg'
                                              width='18'
                                              height='18'
                                              viewBox='0 0 24 24'
                                              fill='none'
                                              stroke='currentColor'
                                              stroke-width='2'
                                              stroke-linecap='round'
                                              stroke-linejoin='round'
                                              className='icon icon-tabler icons-tabler-outline icon-tabler-download mx-1 mb-1'
                                            >
                                              <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                              <path d='M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-2' />
                                              <path d='M7 11l5 5l5 -5' />
                                              <path d='M12 4l0 12' />
                                            </svg>{' '}
                                            Download
                                          </button>
                                        </li>
                                        <li>
                                          <button
                                            className={`dropdown-item ${
                                              currentUser?.role === 'Auditor' ? 'text-gray-600' : ''
                                            } `}
                                            type='button'
                                            style={{color: '#F15363'}}
                                            onClick={() =>
                                              handleRemoveFileClick(index, file, temp.id)
                                            }
                                            disabled={currentUser?.role === 'Auditor'}
                                          >
                                            <svg
                                              xmlns='http://www.w3.org/2000/svg'
                                              width='18'
                                              height='18'
                                              viewBox='0 0 24 24'
                                              fill='none'
                                              stroke='currentColor'
                                              strokeWidth='2'
                                              strokeLinecap='round'
                                              strokeLinejoin='round'
                                              className='icon icon-tabler icons-tabler-outline icon-tabler-trash mx-1 mb-1'
                                            >
                                              <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                              <path d='M4 7l16 0' />
                                              <path d='M10 11l0 6' />
                                              <path d='M14 11l0 6' />
                                              <path d='M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12' />
                                              <path d='M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3' />
                                            </svg>{' '}
                                            Remove
                                          </button>
                                        </li>
                                      </ul>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          </React.Fragment>
                        ))}
                      </>
                    )}

                    {(errorMessages[id] ||
                      (localFileInfo.length === 0 && !isFileUploading && !isUpdatingOwner)) && (
                      (currentUser?.role === 'Auditor') ? (
                        <div className='text-center py-8'>
                          <div className='mb-4'>
                            <img 
                              src='/media/buttons/EmptyState-File.svg' 
                              alt='No evidence' 
                              style={{width: '40px', height: '40px'}}
                            />
                          </div>
                          <div className='text-gray-900 fs-6 fw-semibold mb-2'>No Policy Available</div>
                          <div className='text-gray-900 fs-7'>No policy has been uploaded for this policy yet.</div>
                        </div>
                      ) : (
                        <Dropzone
                          onDrop={handleUpload}
                          // minSize={64}
                          accept={{
                            'application/pdf': [],
                            'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
                              [],
                          }}
                          // maxSize={5 * 1024 * 1024}
                          disabled={currentUser?.role === 'Auditor'}
                        >
                          {({getRootProps, getInputProps}) => (
                            <div
                              {...getRootProps({
                                className: 'dropzone',
                                style: {
                                  backgroundColor: '#fff',
                                  padding: '20px',
                                  border: '2px dashed #ccc',
                                  borderRadius: '4px',
                                  textAlign: 'center',
                                },
                              })}
                            >
                              <input {...getInputProps()} />
                              <img
                                src='/media/icon/uploadFile.svg'
                                alt=''
                                loading='lazy'
                                className='mb-4 mt-4'
                              />
                              <p className='fw-bold fs-6'>
                                Drag and drop or <span style={{color: '#0e9f6e'}}>select</span> a
                                files
                              </p>
                              <p className='fs-6'>Upload upto 10 MB. Supported formats: docx, pdf</p>
                            </div>
                          )}
                        </Dropzone>
                      )
                    )}
                  </div>
                </div>
                <div className='separator my-8'></div>

                <div className='form-group mb-4'>
                  <label className='text-dark fw-bold mb-2 fs-6'>Owner</label>
                  <div className='fs-6 fw-semibold mb-2' style={{color: '#5A5A5A'}}>
                    Policy owner will be responsible for reviewing and approving documents.
                  </div>

                  <select
                    name='group'
                    className='form-select'
                    data-control='select2'
                    data-placeholder='Select Owner'
                    onChange={handleOwnerChange}
                    value={owner}
                    disabled={currentUser?.role === 'Auditor'}
                  >
                    <option value='' selected>
                      Select Owner
                    </option>
                    {owners
                      .filter((user) => user.role === 'Admin' || user.role === 'Owner')
                      .map((owner) => (
                        <option key={owner.id} value={owner.full_name}>
                          {owner.full_name}
                        </option>
                      ))}
                  </select>
                </div>

                <div className='separator my-8'></div>

                <div className='form-group mb-15'>
                  <label className='fw-semibold mb-2 fs-6'>Next Review</label>
                  <div className='fs-6 fw-semibold mb-2' style={{color: '#5A5A5A'}}>
                    Please select a review date. Ideally, you are required to review your policies
                    anually.
                  </div>
                  <div className=''>
                    <span className='fw-normal'>
                      {' '}
                      <select
                        name='timeframe'
                        className={`form-select ${errorMessages[id] ? 'border-danger' : ''}`}
                        data-control='select2'
                        data-placeholder='Select time interval'
                        value={timeframe}
                        onChange={handleTimeframe}
                        disabled={currentUser?.role === 'Auditor'}
                      >
                        <option value='' selected>
                          Select time interval
                        </option>
                        <option value='Annually'>Annually (Recommended)</option>
                        <option value='Bi-annually'>Bi-annually</option>
                        <option value='Quarterly'>Quarterly</option>
                        <option value='Monthly'>Monthly</option>
                        <option value='Weekly'>Weekly</option>
                        <option value='Never'>Never</option>
                      </select>
                    </span>
                    <div className='fs-7' style={{color: '#828282'}}>
                      Suggested review date: {reviewDate} (Auto updates after file is uploaded)
                    </div>
                    {errorMessages[id] && (
                      <div className='text-danger mt-1 fs-6'>{errorMessages[id]}</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        ))}
      </Drawer>
      <ToastContainer />
      <UpgradePlan show={showModal} onClose={() => setShowModal(false)} />
      <ConfirmationModal
        isModalOpen={isEvidenceDeleteOpen}
        hideModal={() => {
          setIsEvidenceDeleteOpen(false)
          setFileToDelete(null)
        }}
        confirmBtnClickHandler={handleConfirmRemoveFile}
        toDeleteItemTitle={fileToDelete?.file?.name}
        isLoading={isDeleting}
        title='Remove Evidence'
        message={`Are you sure you want to remove "${fileToDelete?.file?.name}"? This action cannot be undone.`}
        confirmButtonText='Remove'
        type='delete'
      />
      <ConfirmationModal
        isModalOpen={isPolicyDeleteOpen}
        hideModal={() => {
          setIsPolicyDeleteOpen(false)
          setSelectedPolicyToDelete(null)
        }}
        confirmBtnClickHandler={handleConfirmDelete}
        toDeleteItemTitle={selectedPolicyToDelete?.policy}
        isLoading={isDeleting}
        confirmButtonText='Remove'
      />
      
      {/* Document Viewer Modal */}
      <DocumentViewerModal
        show={showDocumentViewer}
        onClose={() => {
          setShowDocumentViewer(false)
          setSelectedFile(null)
        }}
        file={selectedFile}
      />
    </>
  )
}

export default PolicyDrawer
